import type { LibraryFolder } from '../../api/library'
import type { Reading, SearchOrder } from '../../api/search'
import { destinationsAmong } from '../library/place'
import { ORDERS } from './place'

/**
 * The controls of the detailed search — and they write into the search line rather than beside it.
 *
 * That is the whole idea. There is no second state anywhere: choosing `Semester 3` from the list puts
 * `folder:"Semester 3"` into the field, which means the address carries it, a link somebody sends carries
 * it, and **the search language is learned by clicking**. Somebody who has used the folder list twice has
 * seen what to type the third time.
 *
 * The order is the one thing that is not written into the line, deliberately: it belongs to the person
 * looking rather than to the question, and a sort order typed into a search box would be a word that
 * silently disappeared from the search.
 */
export interface FiltersProps {
  readonly line: string
  readonly reading: Reading | null
  readonly order: SearchOrder
  readonly folders: readonly LibraryFolder[]
  readonly onLine: (line: string) => void
  readonly onOrder: (order: SearchOrder) => void
}

export function Filters({ line, reading, order, folders, onLine, onOrder }: FiltersProps) {
  const folderNow = valueOf(reading, 'folder')
  const createdNow = valueOf(reading, 'created')

  return (
    <div className="filters">
      <label className="field">
        <span className="field__label">In a folder</span>
        <select
          className="field__input"
          value={folderNow}
          onChange={(event) => {
            onLine(withFilter(line, reading, 'folder', quoted(event.target.value)))
          }}
        >
          <option value="">Everywhere</option>
          {destinationsAmong(folders).map((folder) => (
            <option key={folder.id} value={nameOf(folder.label)}>
              {folder.label}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field__label">From</span>
        <input
          className="field__input"
          type="text"
          inputMode="numeric"
          placeholder="2025-11, 2025 or 10.11.2025"
          defaultValue={createdNow}
          onBlur={(event) => {
            onLine(withFilter(line, reading, 'created', event.target.value.trim()))
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onLine(withFilter(line, reading, 'created', event.currentTarget.value.trim()))
            }
          }}
        />
      </label>

      <fieldset className="filters__order">
        <legend className="field__label">Order</legend>
        <div className="controls controls--tight">
          {ORDERS.map((one) => (
            <button
              key={one.value}
              type="button"
              className={one.value === order ? 'control control--quiet' : 'control control--bare'}
              aria-pressed={one.value === order}
              onClick={() => {
                onOrder(one.value)
              }}
            >
              {one.label}
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  )
}

/** What this filter says in the line right now, as the service read it — or nothing. */
function valueOf(reading: Reading | null, key: string): string {
  return reading?.filters.find((filter) => filter.key === key)?.value ?? ''
}

/**
 * The line with this filter set to [value] — replaced where it stands, added at the end, or taken out.
 *
 * Replaced *where it stands* rather than appended, so that using a control twice does not leave two
 * contradictory tokens behind for somebody to puzzle over.
 */
function withFilter(line: string, reading: Reading | null, key: string, value: string): string {
  const token = value === '' ? '' : `${key}:${value}`
  const standing = reading?.filters.find((filter) => filter.key === key)

  if (standing === undefined) {
    return token === '' ? line : `${line.trim()} ${token}`.trim()
  }
  const written = `${line.slice(0, standing.from)}${token}${line.slice(standing.to)}`
  return written.replace(/\s{2,}/g, ' ').trim()
}

/** A value with a space in it needs quotation marks, or only its first word would be the value. */
function quoted(value: string): string {
  if (value === '') {
    return ''
  }
  return /\s/.test(value) ? `"${value}"` : value
}

/** A folder is offered by its whole path and written by its own name, which is what the filter reads. */
function nameOf(path: string): string {
  return path.split(' / ').at(-1) ?? path
}
